import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import './amplify';
import { App } from './app-root';
import { reportAgentQueryResult } from './components/alliance/agent-access';
import {
  reportRequestFailure,
  reportRequestSuccess,
} from './components/alliance/connection-monitor';
import { AuthProvider } from './contexts/auth-context';
import { materialTheme } from './theme';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry once, but not 4xx: the API answered, and asking again only
      // doubles the error logs and delays the message
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        return failureCount < 1 && !(status && status >= 400 && status < 500);
      },
      refetchOnWindowFocus: false,
    },
  },
  // Feed the connection banner and the impersonation access check (noAgent).
  // Every query and mutation already reports its own errors with
  // captureError in onError, so nothing is logged here
  queryCache: new QueryCache({
    onError: (error, query) => {
      reportRequestFailure(error);
      reportAgentQueryResult({ queryKey: query.queryKey, error });
    },
    onSuccess: (data, query) => {
      reportRequestSuccess();
      reportAgentQueryResult({ queryKey: query.queryKey });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => reportRequestFailure(error),
    onSuccess: () => reportRequestSuccess(),
  }),
});

createRoot(document.getElementById('root')!).render(
  <ThemeProvider theme={materialTheme}>
    <CssBaseline />

    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </HelmetProvider>
    </QueryClientProvider>
  </ThemeProvider>,
);
